// frontend/src/utils/membershipUtils.js
import { MEMBERSHIP_FRIENDLY_NAMES, PACKAGE_FRIENDLY_NAMES } from "./constants";

/**
 * Get friendly name for a membership type
 * @param {string} membershipType - Membership type code
 * @returns {string} - Friendly name for the membership type
 */
export const getMembershipFriendlyName = (membershipType) => {
  return MEMBERSHIP_FRIENDLY_NAMES[membershipType] || membershipType;
};

/**
 * Get friendly name for a package type
 * @param {string} packageName - Package type code
 * @returns {string} - Friendly name for the package type
 */
export const getPackageFriendlyName = (packageName) => {
  return PACKAGE_FRIENDLY_NAMES[packageName] || packageName;
};

/**
 * Calculate remaining sessions for a membership or package
 * @param {Object} usage - Usage object with remaining, booked, completed, and forfeited properties
 * @returns {Object} - Object with total, used, and remaining properties
 */
export const calculateRemainingSessionsInfo = (usage) => {
  if (!usage || typeof usage !== "object") {
    return { totalSessions: 0, usedSessions: 0, remainingSessions: 0 };
  }

  const forfeitedSessions = usage.forfeited || 0;

  if (usage.totalSessions === 31) {
    return {
      totalSessions: "unlimited",
      usedSessions: usage.booked + usage.completed + forfeitedSessions,
      remainingSessions: "unlimited",
    };
  }

  // If totalSessions is available, use it; otherwise, calculate it from the other fields
  const totalSessions =
    usage.totalSessions !== undefined
      ? usage.totalSessions
      : usage.remaining + usage.booked + usage.completed + forfeitedSessions;

  // Prefer the DB's authoritative `remaining` field over a derived value. If
  // a prior package exhausted and was repurchased on top of a stale bucket,
  // the computed value (totalSessions - booked - completed - forfeited) can
  // go negative because the counters aren't always reset in lockstep with
  // totalSessions. `usage.remaining` is the source of truth the booking
  // cascade already uses (`remaining > 0` guards) — mirror it here.
  const remainingSessions =
    typeof usage.remaining === "number"
      ? usage.remaining
      : totalSessions - (usage.booked + usage.completed + forfeitedSessions);
  const usedSessions = Math.max(totalSessions - remainingSessions, 0);

  return { totalSessions, usedSessions, remainingSessions };
};

/**
 * Format remaining sessions for display
 * @param {Object} usage - Usage object with remaining, booked, completed, and forfeited properties
 * @returns {string} - Formatted string for display
 */
export const formatRemainingSessionsDisplay = (usage) => {
  // If usage is not an object, return a safe string
  if (!usage || typeof usage !== "object") {
    return "0";
  }

  const info = calculateRemainingSessionsInfo(usage);

  if (!info || typeof info !== "object") return "0";

  const { totalSessions, usedSessions, remainingSessions } = info;

  if (totalSessions === "unlimited" || remainingSessions === "unlimited") {
    return "UNLMTD";
  }

  // Ensure these are numbers
  if (
    typeof totalSessions !== "number" ||
    typeof usedSessions !== "number" ||
    typeof remainingSessions !== "number"
  ) {
    return "Invalid";
  }

  return String(`${remainingSessions} / ${totalSessions}`); //TODO:: Update this on April 30th
  //return String(`${totalSessions} / ${totalSessions}`);
};

/**
 * Check if a membership or package has available or booked sessions
 * @param {Object} profile - User profile object
 * @param {string} type - Type of membership or package ("sauna" or "redLight")
 * @param {string} category - Category ("membership", "package", "dropIn", or "gifted")
 * @returns {boolean} - True if the membership or package has available or booked sessions
 */
export const hasAvailableSessions = (profile, type, category) => {
  if (!profile) return false;

  let usage;
  let typeCheck = false;

  if (type === "shared") {
    if (category === "membership") {
      usage = profile.sharedMembershipSessionUsage;
      typeCheck =
        profile.creditSystemType === "unified_shared" &&
        ((profile.sharedMembershipCurrentPeriodEnd &&
          new Date(profile.sharedMembershipCurrentPeriodEnd) > new Date()) ||
          (usage && usage.booked > 0) ||
          (usage && usage.remaining > 0));
    } else if (category === "package") {
      usage = profile.sharedPackageSessionUsage;
      typeCheck =
        profile.sharedPackageName && profile.sharedPackageName !== "none";
    } else if (category === "dropIn") {
      usage = profile.sharedDropInCount;
      return (
        (usage?.remaining || 0) > 0 ||
        (usage?.booked || 0) > 0 ||
        (usage?.forfeited || 0) > 0
      );
    } else if (category === "gifted") {
      usage = profile.sharedGiftedCredits;
      return (
        (usage?.remaining || 0) > 0 ||
        (usage?.booked || 0) > 0 ||
        (usage?.forfeited || 0) > 0
      );
    }
  }

  if (category === "membership") {
    if (type === "sauna") {
      // Show if membership type is not none AND (has active status OR has booked sessions)
      typeCheck =
        profile.saunaMembershipType !== "none" &&
        (profile.saunaMembershipStatus === "active" ||
          profile.saunaMembershipStatus === "trialing" ||
          (profile.saunaMembershipSessionUsage &&
            profile.saunaMembershipSessionUsage.booked > 0));
      usage = profile.saunaMembershipSessionUsage;
    } else if (type === "redLight") {
      // Show if membership type is not none AND (has active status OR has booked sessions)
      typeCheck =
        profile.redLightMembershipType !== "none" &&
        (profile.redLightMembershipStatus === "active" ||
          profile.redLightMembershipStatus === "trialing" ||
          (profile.redLightMembershipSessionUsage &&
            profile.redLightMembershipSessionUsage.booked > 0));
      usage = profile.redLightMembershipSessionUsage;
    }
  } else if (category === "package") {
    if (type === "sauna") {
      typeCheck =
        profile.saunaPackageName && profile.saunaPackageName !== "none";
      usage = profile.saunaPackageSessionUsage;
    } else if (type === "redLight") {
      typeCheck =
        profile.redLightPackageName && profile.redLightPackageName !== "none";
      usage = profile.redLightPackageSessionUsage;
    }
  } else if (category === "dropIn") {
    if (type === "sauna") {
      usage = profile.dropInSaunaCount;
    } else if (type === "redLight") {
      usage = profile.dropInRedLightCount;
    }

    if (usage) {
      // Show if there are remaining, booked, or forfeited sessions
      return usage.remaining > 0 || usage.booked > 0 || (usage.forfeited || 0) > 0;
    }

    return false;
  } else if (category === "gifted") {
    if (type === "sauna") {
      usage = profile.giftedSaunaCredits;
    } else if (type === "redLight") {
      usage = profile.giftedRedLightCredits;
    }

    if (usage) {
      // Show if there are remaining, booked, or forfeited sessions
      return usage.remaining > 0 || usage.booked > 0 || (usage.forfeited || 0) > 0;
    }

    return false;
  }

  if (!typeCheck || !usage) return false;

  // Check if unlimited, has remaining sessions, booked sessions, or forfeited sessions
  return (
    usage.totalSessions === 31 ||
    usage?.remaining > 0 ||
    usage.booked > 0 ||
    (usage.forfeited || 0) > 0
  );
};
